/**
 * Making a received email safe to render, and readable once it is.
 *
 * Two jobs, both about the same string — the `body_html` we store verbatim from
 * Graph, written by whoever emailed the clinic.
 *
 * ⚠️ SANITISE. This HTML is authored by strangers and was being handed straight
 * to dangerouslySetInnerHTML in a super-admin console that keeps its auth token
 * in localStorage. Browsers do not execute <script> inserted via innerHTML, but
 * they very much fire `onerror` on a broken <img>, follow `javascript:` hrefs and
 * load <iframe>s — so "it looked fine in testing" proves nothing. Everything here
 * is an ALLOW-list: unknown tags are unwrapped, unknown attributes dropped.
 *
 * Parsing happens in an inert document (DOMParser), so nothing loads or executes
 * while we clean it — only the sanitised copy ever reaches the page.
 *
 * TRIM THE QUOTE. Every reply carries the whole prior chain beneath it, and a
 * thread view already shows those messages in their own right. Left alone, each
 * message is mostly a worse copy of the ones above it. We find the boundary and
 * let the caller fold everything past it away.
 */

// Tags worth keeping in an email body. Everything else is unwrapped (children
// kept, element dropped) rather than deleted, so removing an unknown wrapper
// never silently eats the message inside it.
const ALLOWED_TAGS = new Set([
  "A", "ABBR", "B", "BLOCKQUOTE", "BR", "CAPTION", "CENTER", "CODE", "DD", "DIV",
  "DL", "DT", "EM", "FONT", "H1", "H2", "H3", "H4", "H5", "H6", "HR", "I", "IMG",
  "LI", "OL", "P", "PRE", "S", "SMALL", "SPAN", "STRONG", "SUB", "SUP", "TABLE",
  "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "U", "UL",
]);

// Dropped WITH their contents — a <script>'s text is not worth keeping, and a
// <style> block would leak the sender's CSS into our own page.
const DROP_ENTIRELY = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "APPLET", "FORM", "INPUT",
  "BUTTON", "TEXTAREA", "SELECT", "LINK", "META", "BASE", "SVG", "MATH", "AUDIO",
  "VIDEO", "SOURCE", "TRACK", "NOSCRIPT", "TEMPLATE", "FRAME", "FRAMESET",
]);

const ALLOWED_ATTRS = new Set([
  "href", "src", "alt", "title", "width", "height", "align", "valign", "border",
  "cellpadding", "cellspacing", "colspan", "rowspan", "color", "face", "style",
  "dir", "lang",
]);

const SAFE_URL = /^(https?:|mailto:|tel:|cid:|#|\/)/i;

function safeUrl(value) {
  const v = (value || "").trim();
  if (!v) return null;
  // data: is allowed for images only, and only image types — a data:text/html
  // href is a script-execution vector dressed as a link.
  if (/^data:image\/(png|jpe?g|gif|webp|bmp);base64,/i.test(v)) return v;
  return SAFE_URL.test(v) ? v : null;
}

function cleanStyle(value) {
  const v = (value || "");
  // Old IE expression(), and any url(javascript:…) — cheap to strip, and the
  // cost of being wrong is script execution.
  if (/expression\s*\(|javascript:|behaviou?r\s*:|@import/i.test(v)) return null;
  return v.slice(0, 2000);
}

/**
 * The raw markup as an inert tree. Nothing executes and nothing loads — DOMParser
 * builds a detached document — so it is safe to READ before it is cleaned.
 *
 * Reading first is the point: the quote markers are `class` and `id` values
 * (.gmail_quote, #divRplyFwdMsg), and sanitising strips both. Cleaning before
 * splitting destroys the very hooks the split depends on — which is exactly the
 * bug this ordering exists to avoid.
 */
function parseInert(html) {
  return new DOMParser().parseFromString(
    `<!doctype html><body>${html || ""}</body>`, "text/html").body;
}

/** Sanitise a container in place and return its HTML. */
function cleanContainer(body) {
  // Walk depth-first over a static list — the tree is mutated as we go.
  const all = Array.prototype.slice.call(body.querySelectorAll("*"));
  all.forEach(function (el) {
    const tag = el.tagName;
    if (DROP_ENTIRELY.has(tag)) {
      el.parentNode && el.parentNode.removeChild(el);
      return;
    }
    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: keep what it contained, lose the element itself.
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      return;
    }
    Array.prototype.slice.call(el.attributes).forEach(function (attr) {
      const name = attr.name.toLowerCase();
      // Every on* handler, without trying to enumerate them.
      if (name.indexOf("on") === 0 || !ALLOWED_ATTRS.has(name)) {
        el.removeAttribute(attr.name);
        return;
      }
      if (name === "href" || name === "src") {
        const url = safeUrl(attr.value);
        if (url === null) el.removeAttribute(attr.name);
        else el.setAttribute(attr.name, url);
      } else if (name === "style") {
        const style = cleanStyle(attr.value);
        if (style === null) el.removeAttribute("style");
        else el.setAttribute("style", style);
      }
    });
    if (tag === "A") {
      // Someone else's link, opened from our console.
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer nofollow");
    }
  });
  return body.innerHTML;
}

/** Sanitised HTML for a list of sibling nodes. */
function cleanNodes(nodes) {
  const holder = document.createElement("div");
  nodes.forEach((n) => holder.appendChild(n.cloneNode(true)));
  return cleanContainer(holder);
}

// Where a reply stops being new and starts being the chain underneath it. Our
// mail comes through Exchange, so Outlook's markers matter most, but a clinic
// replying from Gmail or Apple Mail is entirely normal — hence the rest.
const QUOTE_SELECTORS = [
  "#divRplyFwdMsg",            // Outlook: "From: … Sent: … To: …" header block
  "#OLK_SRC_BODY_SECTION",     // Outlook for Mac
  ".OutlookMessageHeader",
  ".gmail_quote",
  ".gmail_attr",
  ".moz-cite-prefix",          // Thunderbird
  "blockquote[type=cite]",     // Apple Mail
  "hr#stopSpelling",           // older Outlook separator
];

const QUOTE_TEXT = [
  /^-{2,}\s*original message\s*-{2,}/i,
  /^_{5,}$/,
  /^from:\s.+\bsent:\s/is,
  /^on\s.{4,80}\bwrote:\s*$/i,
];

/** True when this top-level node begins the quoted chain. */
function startsQuote(el) {
  if (el.nodeType !== 1) return false;
  for (let i = 0; i < QUOTE_SELECTORS.length; i += 1) {
    if (el.matches && el.matches(QUOTE_SELECTORS[i])) return true;
    if (el.querySelector && el.querySelector(QUOTE_SELECTORS[i])) return true;
  }
  // Outlook draws the divider as a border rather than an <hr>.
  const style = (el.getAttribute && el.getAttribute("style")) || "";
  if (/border-top:\s*(solid|1px|1\.0pt)/i.test(style) && /#(e1e1e1|b5c4df|ccc)/i.test(style)) {
    return true;
  }
  const text = (el.textContent || "").trim();
  return QUOTE_TEXT.some((re) => re.test(text));
}

/**
 * ``{ visible, quoted }`` — sanitised HTML for the new part and the chain below.
 *
 * `quoted` is "" when there is nothing to fold. If EVERYTHING looks quoted the
 * whole thing is returned as visible: a message collapsed to nothing reads as a
 * bug, and being over-eager here costs more than leaving a quote showing.
 */
export function splitEmailBody(html) {
  if (!html) return { visible: "", quoted: "" };

  let body;
  try {
    body = parseInert(html);
  } catch (e) {
    // A body we cannot parse is a body we will not inject.
    return { visible: "", quoted: "" };
  }

  const children = Array.prototype.slice.call(body.childNodes);
  let cut = -1;
  for (let i = 0; i < children.length; i += 1) {
    if (startsQuote(children[i])) { cut = i; break; }
  }

  if (cut === -1) return { visible: cleanNodes(children), quoted: "" };

  const visible = cleanNodes(children.slice(0, cut));
  // Everything looked like a quote. Collapsing a message to nothing reads as a
  // bug, and being over-eager here costs more than leaving a quote showing.
  if (!visible.replace(/<[^>]*>/g, "").trim()) {
    return { visible: cleanNodes(children), quoted: "" };
  }
  return { visible, quoted: cleanNodes(children.slice(cut)) };
}

/** Sanitised HTML, without splitting. For bodies we render whole. */
export function sanitizeEmailHtml(html) {
  if (!html) return "";
  try {
    return cleanContainer(parseInert(html));
  } catch (e) {
    return "";
  }
}
